import { Check, Clock } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type { DaypartKey } from '@/features/reports/lib/dayparts'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  DAYPART_PRESETS,
  daypartRangeLabel,
} from '@/features/reports/lib/dayparts'
import { cn } from '@/lib/utils'

interface TimeWindowValue {
  daypart: DaypartKey | null
  hourFrom: number | null
  hourTo: number | null
}

interface TimeWindowFilterProps {
  value: TimeWindowValue
  onChange: (next: TimeWindowValue) => void
}

/**
 * The time-of-day window (CHG-2026-09-manager-reporting).
 *
 * Presets as rows, because nobody wants to type two times to ask "how was lunch". The free window
 * sits below a hairline for the case a preset does not fit.
 *
 * Deliberately **not** derived from the store's staffed-hours schedule: that configuration is
 * current-only and not historised, so applying today's opening hours to last month's revenue would
 * produce a confident number about a schedule that may never have existed.
 */
export function TimeWindowFilter({ value, onChange }: TimeWindowFilterProps) {
  const { t } = useTranslation('reports')

  const activeLabel = value.daypart
    ? t(`dayparts.${value.daypart}`)
    : value.hourFrom != null || value.hourTo != null
      ? `${pad(value.hourFrom ?? 0)}:00–${pad(value.hourTo ?? 24)}:00`
      : // Its own name while nothing is set, not "Ganztags": the period control beside it now ends
        // in "· Ganztags" too, and two controls showing the same word invite the reader to look for
        // a difference between them that is not there.
        t('timeWindow.label')

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-12 justify-start gap-2 sm:h-9">
          <Clock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          {activeLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <div className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('timeWindow.label')}
        </div>
        <div className="pb-1">
          <PresetRow
            label={t('timeWindow.allDay')}
            selected={
              value.daypart == null &&
              value.hourFrom == null &&
              value.hourTo == null
            }
            onSelect={() =>
              onChange({ daypart: null, hourFrom: null, hourTo: null })
            }
          />
          {DAYPART_PRESETS.map((part) => (
            <PresetRow
              key={part.key}
              label={t(`dayparts.${part.key}`)}
              hint={daypartRangeLabel(part)}
              selected={value.daypart === part.key}
              onSelect={() =>
                onChange({
                  daypart: part.key,
                  hourFrom: part.hourFrom,
                  hourTo: part.hourTo,
                })
              }
            />
          ))}
        </div>
        <div className="border-t border-border p-3">
          <div className="mb-2 text-xs font-medium text-muted-foreground">
            {t('timeWindow.custom')}
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label htmlFor="hour-from" className="text-xs">
                {t('timeWindow.from')}
              </Label>
              <Input
                id="hour-from"
                type="number"
                min={0}
                max={24}
                inputMode="numeric"
                value={value.hourFrom ?? ''}
                onChange={(e) =>
                  onChange({
                    daypart: null,
                    hourFrom: clampHour(e.target.value),
                    hourTo: value.hourTo,
                  })
                }
              />
            </div>
            <div className="flex-1">
              <Label htmlFor="hour-to" className="text-xs">
                {t('timeWindow.to')}
              </Label>
              <Input
                id="hour-to"
                type="number"
                min={0}
                max={24}
                inputMode="numeric"
                value={value.hourTo ?? ''}
                onChange={(e) =>
                  onChange({
                    daypart: null,
                    hourFrom: value.hourFrom,
                    hourTo: clampHour(e.target.value),
                  })
                }
              />
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {t('timeWindow.hint')}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function PresetRow({
  label,
  hint,
  selected,
  onSelect,
}: {
  label: string
  hint?: string
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/60',
        selected && 'font-medium',
      )}
    >
      <Check
        className={cn(
          'h-4 w-4 shrink-0',
          selected ? 'opacity-100' : 'opacity-0',
        )}
        aria-hidden="true"
      />
      <span className="flex-1">{label}</span>
      {hint ? (
        <span className="text-xs tabular-nums text-muted-foreground">
          {hint}
        </span>
      ) : null}
    </button>
  )
}

function clampHour(raw: string): number | null {
  if (raw === '') return null
  const value = Number(raw)
  if (!Number.isFinite(value)) return null
  return Math.max(0, Math.min(24, Math.round(value)))
}

function pad(hour: number): string {
  return String(hour).padStart(2, '0')
}
